import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { deliveryAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

function DeliveryDashboard() {
  const [stats, setStats] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [isAvailable, setIsAvailable] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
    fetchEarnings();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await deliveryAPI.getDashboard();
      setStats(response.data.data);
      setIsAvailable(response.data.data.isAvailable);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEarnings = async () => {
    try {
      const response = await deliveryAPI.getEarningsSummary();
      setEarnings(response.data.data);
    } catch (error) {
      console.error('Failed to fetch earnings:', error);
    }
  };

  const toggleAvailability = async () => {
    try {
      await deliveryAPI.updateAvailability(!isAvailable);
      setIsAvailable(!isAvailable);
      alert(`Availability set to ${!isAvailable ? 'available' : 'unavailable'}`);
    } catch (error) {
      alert('Failed to update availability');
    }
  };

  if (loading) return <div className="loading">🔄 Loading dashboard...</div>;

  return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }} className="fade-in">
          <h1 style={{
            fontSize: 'var(--font-size-4xl)',
            fontWeight: '800',
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: 'var(--space-sm)'
          }}>
            🚴 Delivery Partner Dashboard
          </h1>
          <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-lg)' }}>
            Manage your deliveries and track your earnings
          </p>
        </div>

        {/* Availability Status Card */}
        <div className="card fade-in" style={{
          marginBottom: 'var(--space-2xl)',
          background: isAvailable ? 'var(--gradient-success)' : 'linear-gradient(135deg, #ff6b6b 0%, #e85a5a 100%)',
          color: 'white',
          border: 'none'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 'var(--space-lg)'
          }}>
            <div style={{ flex: 1 }}>
              <h2 style={{
                fontSize: 'var(--font-size-3xl)',
                fontWeight: '800',
                marginBottom: 'var(--space-md)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-sm)'
              }}>
                <span style={{ fontSize: '48px' }}>{isAvailable ? '🟢' : '🔴'}</span>
                <span>{isAvailable ? 'Available for Deliveries' : 'Currently Offline'}</span>
              </h2>
              <p style={{
                fontSize: 'var(--font-size-base)',
                opacity: 0.95,
                margin: 0
              }}>
                {isAvailable
                  ? 'You will receive delivery requests'
                  : 'Go online to start accepting delivery requests'}
              </p>
            </div>
            <button
              onClick={toggleAvailability}
              className="btn btn-lg"
              style={{
                background: 'white',
                color: isAvailable ? 'var(--danger)' : 'var(--success)',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-sm)'
              }}
            >
              <span>{isAvailable ? '⏸️' : '▶️'}</span>
              <span>{isAvailable ? 'Go Offline' : 'Go Online'}</span>
            </button>
          </div>
        </div>

        {/* Payout Summary */}
        {earnings && (
          <div className="card fade-in" style={{ marginBottom: 'var(--space-xl)' }}>
            <h3 style={{
              fontSize: 'var(--font-size-2xl)',
              fontWeight: '700',
              marginBottom: 'var(--space-lg)',
              color: 'var(--gray-900)'
            }}>
              💵 Payout Summary
            </h3>
            {/* Remaining Payout Highlight */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-xl)',
              marginBottom: 'var(--space-lg)',
              border: '2px solid rgba(16, 185, 129, 0.3)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-lg)' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem'
                }}>
                  💰
                </div>
                <div>
                  <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-xs)' }}>
                    Remaining Payout
                  </p>
                  <p style={{
                    fontSize: 'var(--font-size-3xl)',
                    fontWeight: '800',
                    color: '#059669',
                    margin: 0
                  }}>
                    ₹{(earnings.remainingPayout || 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 'var(--space-md)' }}>
              <div className="stat-card" style={{ borderLeftColor: 'var(--primary)' }}>
                <div className="stat-icon">💎</div>
                <div className="stat-value">₹{(earnings.totalEarnings || 0).toLocaleString()}</div>
                <div className="stat-label">Total Earnings</div>
              </div>
              <div className="stat-card" style={{ borderLeftColor: 'var(--success)' }}>
                <div className="stat-icon">✅</div>
                <div className="stat-value">₹{(earnings.totalPaidOut || 0).toLocaleString()}</div>
                <div className="stat-label">Total Paid Out</div>
              </div>
              <div className="stat-card" style={{ borderLeftColor: 'var(--warning)' }}>
                <div className="stat-icon">📅</div>
                <div className="stat-value">₹{(earnings.todayEarnings || 0).toLocaleString()}</div>
                <div className="stat-label">Today's Earnings</div>
              </div>
            </div>
          </div>
        )}

        {/* Gig System Progress */}
        {earnings?.gigStats && (
          <div className="card fade-in" style={{ marginBottom: 'var(--space-xl)' }}>
            <h3 style={{
              fontSize: 'var(--font-size-2xl)',
              fontWeight: '700',
              marginBottom: 'var(--space-lg)',
              color: 'var(--gray-900)'
            }}>
              🎯 Gig System Progress
            </h3>

            {/* Online Hours Progress */}
            <div style={{
              padding: 'var(--space-lg)',
              background: 'var(--gray-50)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-md)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-sm)' }}>
                <span style={{ fontWeight: '600', color: 'var(--gray-700)' }}>Weekly Online Hours</span>
                <span style={{ fontWeight: '700', color: 'var(--primary)' }}>
                  {earnings.gigStats.onlineHoursThisWeek || 0}h / {earnings.gigStats.minHoursPerWeek || 0}h
                </span>
              </div>
              <div style={{
                width: '100%',
                height: '12px',
                background: 'var(--gray-200)',
                borderRadius: 'var(--radius-full)',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${Math.min(100, ((earnings.gigStats.onlineHoursThisWeek || 0) / (earnings.gigStats.minHoursPerWeek || 1)) * 100)}%`,
                  height: '100%',
                  background: 'var(--gradient-primary)',
                  borderRadius: 'var(--radius-full)',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>

            {/* Daily Deliveries Progress */}
            <div style={{
              padding: 'var(--space-lg)',
              background: 'var(--gray-50)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-lg)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-sm)' }}>
                <span style={{ fontWeight: '600', color: 'var(--gray-700)' }}>Today's Deliveries</span>
                <span style={{ fontWeight: '700', color: 'var(--secondary)' }}>
                  {earnings.gigStats.deliveriesCompletedToday || 0} / {earnings.gigStats.targetDeliveriesPerDay || 0}
                </span>
              </div>
              <div style={{
                width: '100%',
                height: '12px',
                background: 'var(--gray-200)',
                borderRadius: 'var(--radius-full)',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${Math.min(100, ((earnings.gigStats.deliveriesCompletedToday || 0) / (earnings.gigStats.targetDeliveriesPerDay || 1)) * 100)}%`,
                  height: '100%',
                  background: 'var(--gradient-secondary)',
                  borderRadius: 'var(--radius-full)',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>

            {/* Bonus Status */}
            <div style={{
              padding: 'var(--space-lg)',
              background: earnings.gigStats.bonusEligible
                ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
              borderRadius: 'var(--radius-md)',
              border: earnings.gigStats.bonusEligible
                ? '2px solid rgba(16, 185, 129, 0.3)'
                : '2px solid rgba(239, 68, 68, 0.3)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-md)' }}>
                <div>
                  <p style={{
                    fontSize: 'var(--font-size-lg)',
                    fontWeight: '700',
                    color: earnings.gigStats.bonusEligible ? '#059669' : '#dc2626',
                    marginBottom: 'var(--space-xs)',
                    margin: 0
                  }}>
                    {earnings.gigStats.bonusEligible ? '🎉 Bonus Eligible!' : '⏳ Keep Going!'}
                  </p>
                  <p style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--gray-600)',
                    margin: 0
                  }}>
                    {earnings.gigStats.bonusEligible
                      ? `Completion bonus: ₹${earnings.gigStats.completionBonusAmount || 0}`
                      : `Progress: ${Math.round(earnings.gigStats.progressPercentage || 0)}%`}
                  </p>
                </div>
                {earnings.gigStats.bonusEarned > 0 && (
                  <div style={{
                    padding: 'var(--space-sm) var(--space-lg)',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    borderRadius: 'var(--radius-md)',
                    color: 'white',
                    fontWeight: '700'
                  }}>
                    Bonus Earned: ₹{earnings.gigStats.bonusEarned}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="card fade-in" style={{ marginBottom: 'var(--space-xl)' }}>
          <h3 style={{
            fontSize: 'var(--font-size-2xl)',
            fontWeight: '700',
            marginBottom: 'var(--space-lg)',
            color: 'var(--gray-900)'
          }}>
            📊 Your Performance
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 'var(--space-lg)'
          }}>
            <div className="stat-card" style={{ borderLeftColor: 'var(--primary)' }}>
              <div className="stat-icon">📦</div>
              <div className="stat-value">{stats?.totalDeliveries || 0}</div>
              <div className="stat-label">Total Deliveries</div>
            </div>

            <div className="stat-card" style={{ borderLeftColor: 'var(--secondary)' }}>
              <div className="stat-icon">🚴</div>
              <div className="stat-value">{stats?.todayDeliveries || 0}</div>
              <div className="stat-label">Today's Deliveries</div>
            </div>

            <div className="stat-card" style={{ borderLeftColor: 'var(--success)' }}>
              <div className="stat-icon">💰</div>
              <div className="stat-value">₹{stats?.totalEarnings || 0}</div>
              <div className="stat-label">Total Earnings</div>
            </div>

            <div className="stat-card" style={{ borderLeftColor: 'var(--warning)' }}>
              <div className="stat-icon">⭐</div>
              <div className="stat-value">{stats?.rating || 0}</div>
              <div className="stat-label">Your Rating</div>
            </div>

            <div className="stat-card" style={{ borderLeftColor: '#ff6b6b' }}>
              <div className="stat-icon">💵</div>
              <div className="stat-value">₹{stats?.unsettledAmount || 0}</div>
              <div className="stat-label">Unsettled COD</div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <h3 style={{
          fontSize: 'var(--font-size-2xl)',
          fontWeight: '700',
          marginBottom: 'var(--space-lg)',
          color: 'var(--gray-900)',
          textAlign: 'center'
        }}>
          ⚡ Quick Actions
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: 'var(--space-lg)'
        }}>
          <Link to="/delivery/available" style={{ textDecoration: 'none' }}>
            <div className="card fade-in" style={{
              textAlign: 'center',
              cursor: 'pointer',
              background: 'var(--gradient-primary)',
              color: 'white',
              border: 'none',
              height: '100%'
            }}>
              <div style={{ fontSize: '64px', marginBottom: 'var(--space-md)' }}>📦</div>
              <h2 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: '700',
                marginBottom: 'var(--space-sm)',
                color: 'white'
              }}>
                Available Orders
              </h2>
              <p style={{ margin: 0, opacity: 0.9 }}>
                View and accept delivery requests
              </p>
            </div>
          </Link>

          <Link to="/delivery/my-deliveries" style={{ textDecoration: 'none' }}>
            <div className="card fade-in" style={{
              textAlign: 'center',
              cursor: 'pointer',
              background: 'var(--gradient-secondary)',
              color: 'white',
              border: 'none',
              height: '100%'
            }}>
              <div style={{ fontSize: '64px', marginBottom: 'var(--space-md)' }}>🚗</div>
              <h2 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: '700',
                marginBottom: 'var(--space-sm)',
                color: 'white'
              }}>
                My Deliveries
              </h2>
              <p style={{ margin: 0, opacity: 0.9 }}>
                Track your active and completed deliveries
              </p>
            </div>
          </Link>

          <Link to="/delivery/settlement" style={{ textDecoration: 'none' }}>
            <div className="card fade-in" style={{
              textAlign: 'center',
              cursor: 'pointer',
              background: 'var(--gradient-success)',
              color: 'white',
              border: 'none',
              height: '100%'
            }}>
              <div style={{ fontSize: '64px', marginBottom: 'var(--space-md)' }}>💰</div>
              <h2 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: '700',
                marginBottom: 'var(--space-sm)',
                color: 'white'
              }}>
                COD Settlement
              </h2>
              <p style={{ margin: 0, opacity: 0.9 }}>
                Request payouts for your settled cash deliveries
              </p>
              {stats?.unsettledAmount > 0 && (
                <div style={{
                  marginTop: 'var(--space-md)',
                  padding: 'var(--space-sm)',
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: 'var(--radius-md)',
                  fontWeight: '700',
                  fontSize: 'var(--font-size-sm)'
                }}>
                  💵 ₹{stats.unsettledAmount} available
                </div>
              )}
            </div>
          </Link>

          <Link to="/delivery/profile" style={{ textDecoration: 'none' }}>
            <div className="card fade-in" style={{
              textAlign: 'center',
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              height: '100%'
            }}>
              <div style={{ fontSize: '64px', marginBottom: 'var(--space-md)' }}>👤</div>
              <h2 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: '700',
                marginBottom: 'var(--space-sm)',
                color: 'white'
              }}>
                My Profile
              </h2>
              <p style={{ margin: 0, opacity: 0.9 }}>
                View your delivery partner information
              </p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default DeliveryDashboard;
